import React from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { ArrowLeft, FileText } from 'lucide-react';

const LOGO_URL = '/aira-echo-logo.png';

export default function TermsPage() {
  const lastUpdated = 'September 25, 2026';

  return (
    <div className="min-h-screen bg-[#030014]">
      {/* Header */}
      <header className="sticky top-0 z-50 bg-[#030014]/80 backdrop-blur-xl border-b border-white/10">
        <div className="max-w-4xl mx-auto px-4 py-4 flex items-center justify-between">
          <Link to="/" className="flex items-center gap-3">
            <img src={LOGO_URL} alt="AIRA" className="w-8 h-8" />
            <span className="font-bold text-white">AIRA ECHO</span>
          </Link>
          <Link
            to="/"
            className="flex items-center gap-2 text-gray-400 hover:text-white transition-colors"
          >
            <ArrowLeft className="w-4 h-4" />
            Back to Home
          </Link>
        </div>
      </header>

      {/* Content */}
      <motion.main
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        className="max-w-4xl mx-auto px-4 py-12"
      >
        <div className="flex items-center gap-3 mb-8">
          <FileText className="w-8 h-8 text-primary" />
          <h1 className="text-3xl font-bold text-white">Terms of Service</h1>
        </div>

        <p className="text-gray-400 mb-8">Last updated: {lastUpdated}</p>

        <div className="prose prose-invert max-w-none space-y-8">
          {/* Introduction */}
          <section>
            <h2 className="text-xl font-semibold text-white mb-4">1. Introduction</h2>
            <p className="text-gray-300 leading-relaxed">
              Welcome to AIRA ECHO ("we," "our," or "us"). These Terms of Service ("Terms") govern your access to and use of the AIRA ECHO platform, including our website, applications, and AI-powered services (collectively, the "Service"). By accessing or using our Service, you agree to be bound by these Terms. If you do not agree to these Terms, please do not use our Service.
            </p>
          </section>

          {/* Eligibility */}
          <section>
            <h2 className="text-xl font-semibold text-white mb-4">2. Eligibility</h2>
            <p className="text-gray-300 leading-relaxed">
              You must be at least 18 years old to use our Service. By using the Service, you represent and warrant that you meet this age requirement and have the legal capacity to enter into these Terms. If you are using the Service on behalf of an organization, you represent that you have authority to bind that organization to these Terms.
            </p>
          </section>

          {/* Account Registration */}
          <section>
            <h2 className="text-xl font-semibold text-white mb-4">3. Account Registration</h2>
            <p className="text-gray-300 leading-relaxed mb-4">
              To access certain features of our Service, you must create an account. When creating an account, you agree to:
            </p>
            <ul className="list-disc list-inside text-gray-300 space-y-2 ml-4">
              <li>Provide accurate, current, and complete information</li>
              <li>Maintain and promptly update your account information</li>
              <li>Maintain the security and confidentiality of your login credentials</li>
              <li>Accept responsibility for all activities under your account</li>
              <li>Notify us immediately of any unauthorized use of your account</li>
            </ul>
          </section>

          {/* Acceptable Use */}
          <section>
            <h2 className="text-xl font-semibold text-white mb-4">4. Acceptable Use Policy</h2>
            <p className="text-gray-300 leading-relaxed mb-4">
              You agree not to use our Service to:
            </p>
            <ul className="list-disc list-inside text-gray-300 space-y-2 ml-4">
              <li>Violate any applicable laws, regulations, or third-party rights</li>
              <li>Generate, distribute, or facilitate harmful, abusive, or illegal content</li>
              <li>Engage in harassment, hate speech, or discrimination</li>
              <li>Attempt to gain unauthorized access to our systems or other users' accounts</li>
              <li>Interfere with or disrupt the Service or servers</li>
              <li>Use automated systems (bots, scrapers) without our express permission</li>
              <li>Reverse engineer, decompile, or attempt to extract source code</li>
              <li>Use the Service for competitive analysis or to build similar products</li>
              <li>Generate content that infringes intellectual property rights</li>
              <li>Misrepresent AI-generated content as human-created where disclosure is required</li>
            </ul>
          </section>

          {/* Subscription & Billing */}
          <section>
            <h2 className="text-xl font-semibold text-white mb-4">5. Subscription & Billing</h2>
            <p className="text-gray-300 leading-relaxed mb-4">
              AIRA ECHO offers various subscription tiers with different features and usage limits:
            </p>
            <ul className="list-disc list-inside text-gray-300 space-y-2 ml-4 mb-4">
              <li><strong>Free Tier:</strong> Limited access with daily usage caps</li>
              <li><strong>Creator:</strong> Enhanced limits for content creators</li>
              <li><strong>Pro:</strong> Professional features with higher limits</li>
              <li><strong>Enterprise:</strong> Custom solutions for organizations</li>
            </ul>
            <p className="text-gray-300 leading-relaxed mb-4">
              <strong>Payment Processing:</strong> All payments are processed securely through Stripe. By subscribing, you authorize us to charge your payment method on a recurring basis.
            </p>
            <p className="text-gray-300 leading-relaxed mb-4">
              <strong>Billing Cycle:</strong> Subscriptions are billed monthly or annually, depending on your selection. Your subscription will automatically renew unless cancelled before the renewal date.
            </p>
            <p className="text-gray-300 leading-relaxed">
              <strong>Price Changes:</strong> We reserve the right to modify pricing with 30 days' notice. Existing subscriptions will be honored at the current rate until the next renewal.
            </p>
          </section>

          {/* Refund Policy */}
          <section>
            <h2 className="text-xl font-semibold text-white mb-4">6. Refund Policy</h2>
            <p className="text-gray-300 leading-relaxed mb-4">
              We want you to be satisfied with our Service. Our refund policy is as follows:
            </p>
            <ul className="list-disc list-inside text-gray-300 space-y-2 ml-4">
              <li><strong>7-Day Guarantee:</strong> New subscribers may request a full refund within 7 days of initial purchase if the Service does not meet expectations</li>
              <li><strong>Pro-rated Refunds:</strong> Cancellations after 7 days are not eligible for refunds, but you retain access until the end of your billing period</li>
              <li><strong>Technical Issues:</strong> Refunds may be issued at our discretion for significant service outages or technical failures</li>
              <li><strong>Abuse:</strong> Refunds will not be issued for accounts terminated due to Terms violations</li>
            </ul>
            
            <div className="mt-6 p-4 bg-white/5 rounded-lg border border-white/10">
              <h3 className="text-lg font-semibold text-white mb-3">Refund Processing Timeline</h3>
              <p className="text-gray-300 leading-relaxed">
                Once a refund is approved, the refund will be processed within 5–7 business days. The actual time for the refunded amount to reflect in the customer's bank account or payment method may vary depending on the bank, card issuer, or payment service provider.
              </p>
            </div>
            
            <div className="mt-4 p-4 bg-white/5 rounded-lg border border-white/10">
              <h3 className="text-lg font-semibold text-white mb-3">Travel Bookings & Payments</h3>
              <p className="text-gray-300 leading-relaxed">
                Eligible refunds for unsuccessful bookings or failed payments are handled according to the applicable booking and cancellation terms of the respective travel service (flights, hotels, cabs, insurance, tours). Refund eligibility, timelines, and amounts may vary based on the specific booking conditions, service provider policies, and the nature of the transaction. For travel-related refund inquiries, please contact support with your booking reference.
              </p>
            </div>
            
            <p className="text-gray-300 leading-relaxed mt-4">
              To request a refund, contact support@askaira.com with your account email and reason for the request.
            </p>
          </section>

          {/* Intellectual Property */}
          <section>
            <h2 className="text-xl font-semibold text-white mb-4">7. Intellectual Property</h2>
            <p className="text-gray-300 leading-relaxed mb-4">
              <strong>Our IP:</strong> The Service, including its design, features, code, and content (excluding user-generated content), is owned by AIRA ECHO and protected by intellectual property laws. You may not copy, modify, or distribute our proprietary materials without permission.
            </p>
            <p className="text-gray-300 leading-relaxed mb-4">
              <strong>Your Content:</strong> You retain ownership of content you create using our Service. By using the Service, you grant us a limited license to process, display, and store your content as necessary to provide the Service.
            </p>
            <p className="text-gray-300 leading-relaxed">
              <strong>AI-Generated Content:</strong> Content generated by our AI tools may be used by you for personal or commercial purposes, subject to applicable laws and our Acceptable Use Policy. You are responsible for ensuring your use complies with relevant regulations.
            </p>
          </section>

          {/* Limitation of Liability */}
          <section>
            <h2 className="text-xl font-semibold text-white mb-4">8. Limitation of Liability</h2>
            <p className="text-gray-300 leading-relaxed mb-4">
              TO THE MAXIMUM EXTENT PERMITTED BY LAW:
            </p>
            <ul className="list-disc list-inside text-gray-300 space-y-2 ml-4">
              <li>THE SERVICE IS PROVIDED "AS IS" WITHOUT WARRANTIES OF ANY KIND, EXPRESS OR IMPLIED</li>
              <li>WE DO NOT WARRANT THAT THE SERVICE WILL BE UNINTERRUPTED, ERROR-FREE, OR SECURE</li>
              <li>WE ARE NOT LIABLE FOR ANY INDIRECT, INCIDENTAL, SPECIAL, CONSEQUENTIAL, OR PUNITIVE DAMAGES</li>
              <li>OUR TOTAL LIABILITY SHALL NOT EXCEED THE AMOUNT YOU PAID US IN THE PAST 12 MONTHS</li>
              <li>AI-GENERATED CONTENT MAY CONTAIN ERRORS; YOU ARE RESPONSIBLE FOR VERIFYING ACCURACY</li>
            </ul>
          </section>

          {/* Indemnification */}
          <section>
            <h2 className="text-xl font-semibold text-white mb-4">9. Indemnification</h2>
            <p className="text-gray-300 leading-relaxed">
              You agree to indemnify, defend, and hold harmless AIRA ECHO, its officers, directors, employees, and agents from any claims, damages, losses, or expenses (including legal fees) arising from: (a) your use of the Service; (b) your violation of these Terms; (c) your violation of any third-party rights; or (d) content you create or share using the Service.
            </p>
          </section>

          {/* Termination */}
          <section>
            <h2 className="text-xl font-semibold text-white mb-4">10. Termination</h2>
            <p className="text-gray-300 leading-relaxed mb-4">
              <strong>By You:</strong> You may terminate your account at any time through your account settings or by contacting support. Upon termination, you lose access to the Service and your data may be deleted after a retention period.
            </p>
            <p className="text-gray-300 leading-relaxed mb-4">
              <strong>By Us:</strong> We may suspend or terminate your account if you violate these Terms, engage in fraudulent activity, or for any reason with reasonable notice. In cases of severe violations, termination may be immediate without notice.
            </p>
            <p className="text-gray-300 leading-relaxed">
              <strong>Effect:</strong> Upon termination, your right to use the Service ceases. Sections regarding intellectual property, limitation of liability, and indemnification survive termination.
            </p>
          </section>

          {/* Modifications */}
          <section>
            <h2 className="text-xl font-semibold text-white mb-4">11. Modifications to Terms</h2>
            <p className="text-gray-300 leading-relaxed">
              We may modify these Terms at any time. Material changes will be communicated via email or through the Service at least 14 days before taking effect. Your continued use of the Service after changes become effective constitutes acceptance of the modified Terms. If you disagree with changes, you must stop using the Service.
            </p>
          </section>

          {/* Governing Law */}
          <section>
            <h2 className="text-xl font-semibold text-white mb-4">12. Governing Law & Disputes</h2>
            <p className="text-gray-300 leading-relaxed mb-4">
              These Terms are governed by the laws of the State of Delaware, United States, without regard to conflict of law principles.
            </p>
            <p className="text-gray-300 leading-relaxed">
              Any disputes arising from these Terms or the Service shall be resolved through binding arbitration in accordance with the rules of the American Arbitration Association. You waive any right to participate in class actions or class arbitrations.
            </p>
          </section>

          {/* Contact */}
          <section>
            <h2 className="text-xl font-semibold text-white mb-4">13. Contact Us</h2>
            <p className="text-gray-300 leading-relaxed">
              If you have questions about these Terms, please contact us at:
            </p>
            <div className="mt-4 p-4 bg-white/5 rounded-lg border border-white/10">
              <p className="text-gray-300">AIRA ECHO Legal Team</p>
              <p className="text-gray-300">Email: legal@askaira.com</p>
              <p className="text-gray-300">Support: support@askaira.com</p>
            </div>
          </section>
        </div>

        {/* Footer Links */}
        <div className="mt-12 pt-8 border-t border-white/10 flex flex-wrap gap-6">
          <Link to="/privacy" className="text-primary hover:underline">Privacy Policy</Link>
          <Link to="/" className="text-gray-400 hover:text-white">Home</Link>
          <Link to="/pricing" className="text-gray-400 hover:text-white">Pricing</Link>
        </div>
      </motion.main>
    </div>
  );
}
